import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Button, ButtonGroup, Input } from '@heroui/react';

import { memo, useState } from 'react';
import isEqual from 'react-fast-compare';

import SheetWrapper, {
  SheetWrapperHeader,
} from '@/components/SheetWrapper/index.jsx';
import {
  type EventsRouteArray,
  type EventsRouteArrayItem,
  hooks,
} from '@/store.js';
import { useMemoizedFn } from 'ahooks';
import { planDetailEvent } from '@/pages/PlanDetail/utils/event.js';
import { toast } from 'sonner';
import useConfirm from '@/hooks/useConfirm/index.jsx';
import { ROUTE_OPTIONS } from '@/const.js';

interface IProps {
  planId: string;
  defaultValue?: Array<string[]>;
  id?: string;
  date: string;
  mode?: string;
  selectKey?: string;
  orderIdx?: number;
}

const OPTIONS = ROUTE_OPTIONS;

const SelectMode = memo(
  ({
    onEdit,
    id,
    onClose,
    value,
    selectKey: defaultSelectedKey,
  }: {
    onEdit: () => void;
    id?: string;
    onClose: () => void;
    value?: IProps['defaultValue'];
    selectKey?: string;
  }) => {
    const client = hooks.useClient();
    const [selectedKey, setSelectKey] = useState(defaultSelectedKey);

    const { showModal } = useConfirm({
      // isSubmitting: deleting,
      onSubmit: async () => {
        if (!id) return;
        await client.events.delete(id);
        onClose?.();
        planDetailEvent.emit('requestRefresh');
      },
      title: '确定删除吗?',
    });

    return (
      <>
        <SheetWrapperHeader
          hideOk
          okText='编辑'
          title='选择交通或停留'
          align='left'
          extra={
            <ButtonGroup>
              <Button
                variant='light'
                // color='secondary'
                className='text-gray-500 min-w-0'
                startContent={<i className='ic-round md-edit' />}
                onPress={onEdit}
              >
                {/* 编辑 */}
              </Button>
              <Button
                variant='light'
                className='text-gray-500 min-w-0'
                startContent={<i className='ic-round md-delete' />}
                onPress={showModal}
              />
            </ButtonGroup>
          }
        />
        <div className='pb-10 pt-8 px-6  '>
          {OPTIONS.map((i) => {
            const idx = value?.findIndex((n) => n[0] === i.value);
            const info =
              value && idx !== undefined && idx !== -1 ? value[idx] : undefined;
            const isSelected = selectedKey === i.value;

            return (
              <article
                key={i.label}
                onClick={async () => {
                  setSelectKey(i.value);
                  if (!id) return;
                  (await client.events.get(id).resolved)?.set('route', i.value);
                  onClose?.();
                }}
                className={`flex items-center    py-4 border-2   rounded-[24px]  px-5 ${
                  isSelected
                    ? 'bg-gray-100 text-gray-800 border-black'
                    : 'text-gray-500/60 border-transparent'
                }`}
              >
                <span className='flex items-center font-semibold'>
                  <i className={`ic-round md-${i.icon} text-xl mr-1.5`} />
                  {i.label}
                </span>
                {info && (
                  <span className='flex-grow block ml-3'>
                    {info?.[1]} | {info?.[2]}
                  </span>
                )}

                {info?.[3] && (
                  <Button
                    color='primary'
                    radius='full'
                    className={`min-w-0  ${
                      isSelected ? 'px-2' : 'px-1.5 opacity-30'
                    }`}
                    size='sm'
                    variant={isSelected ? undefined : 'bordered'}
                    startContent={
                      <i className='ic-round md-navigation text-base' />
                    }
                    onPress={() => {
                      window.open(info?.[3]);
                    }}
                  />
                )}
              </article>
            );
          })}
        </div>
      </>
    );
  },
  isEqual
);

const Content = memo(
  ({
    id,
    onClose,
    defaultValue,
    date,
    planId,
    selectKey,
    orderIdx,
    mode: defaultMode,
  }: IProps & { onClose: () => void }) => {
    const [mode, setMode] = useState(defaultMode);

    const client = hooks.useClient();

    const [value, setValue] = useState(defaultValue || []);

    const handleValueChange = useMemoizedFn(
      ({
        value,
        type,
        offset,
      }: {
        value: string;
        type: string;
        offset: number;
      }) => {
        setValue((prev) => {
          const result = [...prev];
          const idx = result.findIndex((n) => n[0] === type);
          if (idx === -1) {
            result.push([
              type,
              ...[...new Array(offset - 1)].map((i) => ''),
              value,
            ]);
          } else {
            const cur = [...result[idx]];
            cur[offset] = value;
            result[idx] = cur;
          }

          return result;
        });
      }
    );

    if (mode === 'select') {
      return (
        <SelectMode
          onEdit={() => {
            setMode('create');
          }}
          id={id}
          onClose={onClose}
          value={value}
          selectKey={selectKey}
        />
      );
    }

    return (
      <>
        <SheetWrapperHeader
          title='设置交通或停留'
          align='left'
          onOk={async () => {
            if (id) {
              (await client.events.get(id).resolved)?.set('routeArray', value);
            } else {
              await client.events.put({
                date,
                planId,
                type: 'route',
                route: value[0]?.[0] || '',
                routeArray: value,
                orderIdx,
              });
            }

            toast.success('操作成功');
            setTimeout(() => {
              planDetailEvent.emit('requestRefresh');
            }, 200);
            onClose();
          }}
        />
        <div className='pb-10 pt-8 px-6 text-gray-800 '>
          <div className='flex text-gray-500/60 font-semibold mb-2 bg-gray-100/70 py-2 text-[13px] px-4 -mx-4 rounded-lg'>
            <span className='flex-grow'>方式</span>
            <span className='w-24 text-right mr-4'>距离</span>
            <span className='w-24 text-right'>时间</span>
          </div>
          {OPTIONS.map((i) => {
            const idx = value.findIndex((n) => n[0] === i.value);

            return (
              <article
                key={i.label}
                className='  py-4 border-b border-gray-200'
              >
                <div className='flex items-center'>
                  <span className='flex items-center flex-grow font-semibold'>
                    <i className={`ic-round md-${i.icon} text-xl mr-1.5`} />
                    {i.label}
                  </span>
                  <Input
                    // endContent='km'
                    placeholder='0km'
                    variant='underlined'
                    className='w-24 text-right mr-4'
                    classNames={{ input: 'text-right' }}
                    defaultValue={value[idx]?.[1]}
                    onValueChange={(e: string) => {
                      handleValueChange({ value: e, type: i.value, offset: 1 });
                    }}
                  />
                  <Input
                    // endContent='min'
                    placeholder='0m'
                    variant='underlined'
                    className='w-24 text-right'
                    classNames={{ input: 'text-right' }}
                    defaultValue={value[idx]?.[2]}
                    onValueChange={(e: string) => {
                      handleValueChange({ value: e, type: i.value, offset: 2 });
                    }}
                  />
                </div>
                <div className='flex justify-end mt-2'>
                  <Input
                    placeholder='Google地图地址'
                    variant='underlined'
                    className='w-[208px] text-right'
                    classNames={{ input: 'text-right' }}
                    defaultValue={value[idx]?.[3]}
                    onValueChange={(e: string) => {
                      handleValueChange({ value: e, type: i.value, offset: 3 });
                    }}
                  />
                </div>
              </article>
            );
          })}
        </div>
      </>
    );
  },
  isEqual
);

const AddRoute = NiceModal.create((props: IProps) => {
  const [closeKey, setCloseKey] = useState(0);
  return (
    <SheetWrapper closeKey={closeKey}>
      <Content {...props} onClose={() => setCloseKey(+new Date())} />
    </SheetWrapper>
  );
});

export const UseAddRoute = () => {
  const modal = useModal(AddRoute);
  return {
    show: modal.show,
  };
};
